/**
 * 内核自带的节点求值器。
 *
 * 现在这里只有**内核**的求值器：结构构造（组 / 循环 / 接通）、调试输出、
 * 以及常量族。领域能力的求值器（数据源、函数、领域组件…）各自住在自己的包里，
 * 由装配点 `playground/nodeTypes.ts` 合并成 `BUILTIN_EVALUATORS`。
 *
 * 新增节点类型只需要往对应的包里加一项，求值层本身不用改。
 *
 * ⚠️ 这个 import 块曾被一次正则删除误伤过：惰性匹配「可选注释 + 目标函数」
 * 会从文件头一路吃到目标函数，把中间所有 import 一起删掉。
 * 改这里时注意别再用那种模式。
 */
import type { NodeRow } from '../types/node'
import { jsDesignTime } from './jsNodeType'
import {
  GROUP_INPUT_NODE,
  GROUP_IN_REPEATER,
  groupParamRows,
  GROUP_OUTPUT_NODE,
  GROUP_OUT_REPEATER,
} from './group'
import {
  LOOP_IN_NODE,
  LOOP_OUT_NODE,
  LOOP_PARAMS,
  loopParamRows,
  runLoop,
  skipEmptyFlags,
  slotCountOf,
} from './loop'
import { repeaterCount, repeaterHandle } from './rows'
import { parseTime } from './timeText'
import type { NodeEvalApi, NodeEvaluator } from './evaluate'



/**
 * 调试查看器：把「监听」变参行收到的值原样透出为输出端口。
 *
 * 它本身不改变数据，只是让值有一个能被读取的落点 ——
 * 展示行再从求值结果里取这些值渲染出来。
 */
const debugViewerEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const out: Record<string, unknown> = {}
  const row = api.model.rows.find((r) => r.kind === 'repeater')
  if (row && row.kind === 'repeater') {
    api.repeat(row.id).forEach((value, index) => {
      const handle = repeaterHandle(row, index)
      if (value !== undefined) out[handle] = value
    })
  }
  return out
}

/**
 * 接通节点：求值就是**直通**。
 *
 * 它的价值全在编辑期（布线），运行时什么都不做 ——
 * 但正因为它是真节点而不是编辑器构造，图引擎才能正常连线、
 * 删除时也才有「上下游接回去」这回事。
 */
const rerouteEvaluator: NodeEvaluator = (api: NodeEvalApi) => ({
  out: api.input('in'),
})

/* ── 示例函数节点 ───────────────────────────────────────────── */




/**
 * 组节点：把外部输入灌进子图的**输入节点**，再把**输出节点**收到的值送出来。
 *
 * 两边按端口 id 一一对应 —— 组节点的 rows 和子图里那两个接口节点的 rows
 * 是同一份接口的两个视图（见 node/group.ts）。
 */
const groupEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const graph = api.model.graph
  if (!graph) return {}

  /*
   * ① 外面喂进来的输入：变参行的每个实例 → 内部输入节点的同名输出行。
   *    用 `api.repeat` 读，它从边推导实例数，跟渲染端一致。
   */
  /*
   * ⚠️ 外面的参数端口现在是**静态输入行**（不是变参行）——
   * 数量由组内部声明，外面连不连都改不了它。
   * 老文档里这一侧是变参行，`groupParamRows` 认不出来时退回 `api.repeat`。
   */
  const feeds: Record<string, unknown> = {}
  const paramRows = groupParamRows(api.model.rows)
  if (paramRows.length > 0) {
    for (const row of paramRows) {
      feeds[row.socket.id] = api.input(row.socket.id)
    }
  } else {
    api.repeat(GROUP_IN_REPEATER).forEach((value, index) => {
      feeds[`${GROUP_IN_REPEATER}.${index}`] = value
    })
  }

  const inner = api.runSubgraph(graph, { [GROUP_INPUT_NODE]: feeds })
  const sink = inner.get(GROUP_OUTPUT_NODE)

  /*
   * ② 里面算出来的结果：输出节点变参行的第 i 个实例 → 外面的第 i 路输出。
   *    按**序号**对应（两边 id 不同：里面是 `out.{i}`，外面是 `out{i}`）。
   */
  const produced: Record<string, unknown> = {}
  api.model.rows
    .filter((row) => row.kind === 'output')
    .forEach((row, index) => {
      produced[row.socket.id] = sink?.get(`${GROUP_OUT_REPEATER}.${index}`)
    })

  return produced
}

/**
 * 组内部的**输入节点**：值全部来自 seeds（组的求值器灌进来的），
 * 所以它自己的求值器不产出任何东西。
 */
const groupInputEvaluator: NodeEvaluator = () => ({})

/**
 * 组内部的**输出节点**：把自己的输入**镜像成输出**。
 *
 * ⚠️ 不镜像的话组永远取不到值 —— 组求值器是拿
 * `inner.get(GROUP_OUTPUT_NODE).get(portId)` 读结果的，
 * 而那个 map 里只会有这个节点**返回**的东西。
 */
const groupOutputEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const out: Record<string, unknown> = {}
  for (const row of api.model.rows) {
    /*
     * 变参行（结果）要**逐个实例**镜像 —— 组求值器读的是
     * `sink.get('out.{i}')`，少一个都取不到值。
     *
     * 显式输入行也一起支持：接口结构以后要是变回去，这里不用跟着改。
     */
    if (row.kind === 'input') {
      out[row.socket.id] = api.input(row.socket.id)
    } else if (row.kind === 'repeater') {
      /*
       * ⚠️ 件数从 `api.repeat()` 拿，**不要**用 `repeaterCount(row)`。
       *
       * 后者读的是 `row.connected` —— 那是**编辑器同步过来的视图状态**。
       * 子图里的行在 headless 求值时没有这份状态（投影只在画布层跑），
       * `repeaterCount` 于是退化成 `min`（结果行是 1）——
       * 表现是「组里接了 3 路结果，外面只有第 1 路有值」。
       *
       * `api.repeat()` 从**边**推导件数（`lastConnectedOf`），
       * 和渲染端、和组求值器读结果那一侧用的是同一条规则。
       */
      const values = api.repeat(row.id)
      for (let i = 0; i < values.length; i += 1) {
        out[repeaterHandle(row, i)] = values[i]
      }
    }
  }
  return out
}

/**
 * 循环节点。
 *
 * 每一轮把 `item` / `index` / `count` 灌给内部的**循环起始节点**（走 seeds），
 * 跑一遍子图，再从**结果累记节点**取回每一路的值。
 *
 * 迭代、步进、以及「跳过空值」都在 `runLoop` 里，那边是纯函数、可单测。
 */
const loopEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const graph = api.model.graph
  if (!graph) return {}

  const rows = api.model.rows
  const slots = slotCountOf(rows)
  if (slots === 0) return {}

  /*
   * 额外参数：用 `api.repeat` 读**变参行**的全部实例值。
   *
   * 它会从边推导实例数（而不是信 `row.connected` 那份视图状态），
   * 所以界面上连了几个就灌几个，序号也对得上。
   */
  /* ⚠️ 同组：外层参数是静态输入行；老文档的变参行形状一并兼容 */
  const params: Record<string, unknown> = {}
  const paramRows = loopParamRows(api.model.rows)
  if (paramRows.length > 0) {
    for (const row of paramRows) params[row.socket.id] = api.input(row.socket.id)
  } else {
    api.repeat(LOOP_PARAMS).forEach((value, i) => {
      params[`${LOOP_PARAMS}.${i}`] = value
    })
  }

  const buckets = runLoop({
    list: api.input('list'),
    step: api.input('step'),
    slots,
    skipEmpty: skipEmptyFlags(rows),
    runOnce(item, index, count) {
      const inner = api.runSubgraph(graph, {
        // 固定三项 + 外面连进来的额外参数，一起灌给「循环起始节点」
        [LOOP_IN_NODE]: { item, index, count, ...params },
      })
      const sink = inner.get(LOOP_OUT_NODE)

      /*
       * handle 用**结果累记节点自己那行**的模板算。
       * 在这里硬编码 'results.{i}' 能跑通，但那是靠巧合 ——
       * 哪天那行的 id 或 handlePattern 改了，这里会静默读不到值。
       */
      const row = graph.nodes
        .find((node) => node.id === LOOP_OUT_NODE)
        ?.rows.find((r) => r.kind === 'repeater')

      return Array.from({ length: slots }, (_, slot) =>
        row?.kind === 'repeater' ? sink?.get(repeaterHandle(row, slot)) : undefined,
      )
    },
  })

  return Object.fromEntries(buckets.map((values, slot) => [`out${slot}`, values]))
}

/** 循环起始节点：值全部来自 seeds，自己不产出 */
const loopStartEvaluator: NodeEvaluator = () => ({})

/** 结果累记节点：把自己的输入镜像成输出（跟组一样） */
const loopOutputEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const out: Record<string, unknown> = {}
  const row = api.model.rows.find((r) => r.kind === 'repeater')
  if (row?.kind === 'repeater') {
    const total = repeaterCount(row)
    for (let i = 0; i < total; i += 1) {
      const handle = repeaterHandle(row, i)
      out[handle] = api.input(handle)
    }
  }
  return out
}

/**
 * **内核求值器** —— 对应 `node/coreNodes.ts` 里的内核节点。
 *
 * ⚠️ 领域包的求值器**不在这里**：它们各自住在包里
 * （例如 `domains/print/evaluators.ts`），由注册表合并成
 * 完整的 `BUILTIN_EVALUATORS`。内核不该知道 `source` 怎么读订单。
 */
/* ══════════════════════════════════════════════════════════════
   常量族
   ══════════════════════════════════════════════════════════════ */

/**
 * 常量节点的求值：把行内控件的字面量原样送出去。
 *
 * ⚠️ 用 `literal('value')` 而**不是** `input('value')`：常量节点虽然也有
 * 一个同名输入行（为了能被人接东西覆盖），但它的语义是「我有这个值」——
 * 真让人接了线，那是接线方的事（`input` 会走回溯）。
 * 这里明确表达「这个节点的产出就是它自己的字面量」。
 */
const literalEvaluator: NodeEvaluator = (api: NodeEvalApi) => ({
  out: api.literal('value'),
})

/**
 * 时间常量：字面量是时间文本，这里解析成 epoch 毫秒。
 *
 * ⚠️ 解析走 `parseTime`（`node/timeText.ts`）而**不是** `Date.parse` ——
 * `Date.parse` 对「月/日没补零」的写法会当**本地时间**，于是
 * `2024-1-2` 和 `2024-01-02` 是两个不同的时刻（在 UTC+8 差一天，
 * 下游取 UTC 年月日就变成 2024-01-01）。详见那个文件顶部的说明。
 *
 * ⚠️ 解析不出来时输出 `null` 而**不是** `NaN`：
 * `NaN` 会在下游悄悄传播（`NaN > 0` 是 false、`String(NaN)` 是 "NaN"），
 * 而 `null` 在调试输出里是明摆着的「这里没有值」。
 *
 * ★★ **留空 = 现在**（2027 加，用户："常量时间这个节点，空着的时候，
 * 能不能输出当前时间"）。
 *
 * 为什么这条语义值得单独写一段：一份"打印当前时间"的模板，用别的方式
 * 表达都很别扭 —— 写死一个时刻是错的（模板存下来那一刻的时间），
 * 靠数据源包又重（为了一行时间装一个包）。而**空着**在界面上本来就是个
 * 可见的状态（占位符写着"留空 = 当前时间"），所以它就该是这个意思。
 *
 * ⚠️ 两个后果，都是刻意的：
 *   · 它是**求值那一刻**的时间：设计期每次重算都会变（预览里能看到它在走），
 *     运行期是这一趟的值 —— 打印作业要的正是后者；
 *   · 于是它**不是**纯函数（同样的输入、不同时刻不同输出）。这在求值器
 *     这一层是可以接受的：纯不纯由**节点类型**决定，`const.datetime`
 *     空着时本来就是"读时钟"这个副作用最小的形态（不改任何外部状态）。
 */
const datetimeEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const text = api.literal('value')
  /* 空串 / 空值 = 不钉死，给"现在"；填了值就老老实实解析（错的还是 null） */
  if (text === undefined || text === null || (typeof text === 'string' && text.trim() === '')) {
    return { out: Date.now() }
  }
  return { out: parseTime(text) }
}

/**
 * 列表常量：把变参行上每一路的输入**收成**一个数组。
 *
 * 两条规则，都是踩过才知道的：
 *
 * ① **去掉尾部的空槽**。`repeat()` 用的是渲染数量
 *    （`repeaterCountFor` = `lastConnected + 2`）—— 末尾**故意多留一个空槽**
 *    好让人继续接线。不收掉的话「连了 3 个」会得到一个长度 4 的列表，
 *    最后一项永远是空的。
 *
 * ② **保留中间的空位**（不压缩）。用户摆了 4 路、只连了第 1 和第 4 路，
 *    长度就是 4 —— 悄悄把空位挤掉会让他对不上号：他摆了几路，
 *    心里就认为是几项。真想跳过空值，那是循环的「跳过空值」开关该做的事。
 */
const listEvaluator: NodeEvaluator = (api: NodeEvalApi) => {
  const items = api.repeat('items')
  let end = items.length
  while (end > 0 && items[end - 1] === undefined) end -= 1
  return { out: items.slice(0, end) }
}

export const CORE_EVALUATORS: Record<string, NodeEvaluator> = {
  /*
   * ★ JS 节点：设计期**空值**（只在 Run 时执行）——
   * 它是"设计期绝不跑副作用"这条规矩的落点之一。
   */
  'core.js': jsDesignTime,
  /* ── 常量族：一种类型一个节点，各自的端口类型固定 ── */
  'const.text': literalEvaluator,
  'const.number': literalEvaluator,
  'const.boolean': literalEvaluator,
  'const.color': literalEvaluator,
  'const.datetime': datetimeEvaluator,
  'const.list': listEvaluator,

  loop: loopEvaluator,
  'loop.start': loopStartEvaluator,
  'loop.output': loopOutputEvaluator,

  'group': groupEvaluator,
  'group.input': groupInputEvaluator,
  'group.output': groupOutputEvaluator,

  reroute: rerouteEvaluator,
  'debug.viewer': debugViewerEvaluator,
}
